import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

export type NotificationImportance = 'info' | 'viktig' | 'kritisk';

export interface NotificationChannelView {
  channel: string;
  status: string;
  sentAtUtc?: string | null;
}

export interface NotificationView {
  id: string;
  type: string;
  module: string;
  importance: NotificationImportance;
  title: string;
  body: string;
  deepLinkRoute: string;
  unread: boolean;
  escalated: boolean;
  createdAtUtc: string;
  channels: NotificationChannelView[];
}

export interface NotificationCentre {
  nye: NotificationView[];
  tidligere: NotificationView[];
}

/** Outbound channel preferences for one (module, importance) cell; all false = "kun i app". */
export interface ChannelPrefs {
  push: boolean;
  email: boolean;
  sms: boolean;
}

export interface QuietHours {
  enabled: boolean;
  startMinute: number;
  endMinute: number;
  criticalOverride: boolean;
}

export interface DigestSettings {
  enabled: boolean;
  hourLocal: number;
}

export interface PreferencesView {
  /** Keyed by `"{module}:{importance}"`. */
  matrix: Record<string, ChannelPrefs>;
  quietHours: QuietHours;
  digest: DigestSettings;
}

export interface DeliveryStatusView {
  total: number;
  delivered: number;
  read: number;
  escalated: number;
}

/**
 * Notification-centre client (`/api/varsler`). Hand-written to mirror the Phase M7 backend
 * contract. The unread count backs the M1 header-bell badge; mark-read/seen mutations keep it
 * from going stale. The dispatch fan-out + escalation are server-side concerns.
 */
@Injectable({ providedIn: 'root' })
export class VarslerService {
  private readonly http = inject(HttpClient);

  centre(): Observable<NotificationCentre> {
    return this.http.get<NotificationCentre>('/api/varsler');
  }

  markRead(id: string): Observable<void> {
    return this.http.post<void>(`/api/varsler/${encodeURIComponent(id)}/lest`, {});
  }

  markAllRead(): Observable<{ count: number }> {
    return this.http.post<{ count: number }>('/api/varsler/marker-alle-lest', {});
  }

  /** Clears the visual badge (seen-on-open) without marking notifications read. */
  setSeen(): Observable<void> {
    return this.http.post<void>('/api/varsler/sett-sett', {});
  }

  updatePreferences(prefs: PreferencesView): Observable<void> {
    return this.http.put<void>('/api/varsler/innstillinger', prefs);
  }

}
